import {
  Checkbox,
  ComboBox,
  CommandBar,
  ContextualMenu,
  ContextualMenuItem,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  FontIcon,
  IButtonProps,
  IButtonStyles,
  IComboBox,
  IComboBoxOption,
  ICommandBarItemProps,
  IContextualMenuItemProps,
  Link,
  mergeStyles,
  Persona,
  PersonaSize,
  PrimaryButton,
  SelectableOptionMenuItemType,
} from "@fluentui/react";
import {
  MgtPerson,
  PeoplePicker,
  Person,
  PersonCardInteraction,
  PersonType,
  UserType,
  ViewType,
} from "@microsoft/mgt-react";
import { useBoolean, useId } from "@fluentui/react-hooks";
import React, { useReducer, useState } from "react";
import { directoryObject } from "../objects/directoryObj";
import { toast } from "react-toastify";
import { useMsal } from "@azure/msal-react";
import { apiScopes } from "../authConfig";
import { getUserByEmail } from "../services/graph";
import Swal from "sweetalert2";
import { renderToStaticMarkup } from "react-dom/server";
import { setAllCheckboxesByClass } from "../utils";
import { resource } from "../objects/resource";
import StatusCode from "status-code-enum";
import { ChangeType } from "../enums";
import { errorsTypes } from "../errorTypes";
import { extendResourceLease } from "../services/resource-manager";

interface GuestManagerProps {
  ownerCount: number;
  spoSiteUrl: string;
  currentOwners: directoryObject[];
  resourceId: string;
  addFunction: (ownerList: directoryObject[], resourceId: string) => void;
  removeFunction: (ownerList: directoryObject[], resourceId: string) => void;
  isM365: boolean;
  isAGS: boolean;
  refreshDatasetFunction: (
    resourceID: string,
    changeType: ChangeType,
    value: any
  ) => void;
}

const CustomMenuItem: React.FunctionComponent<IContextualMenuItemProps> = (
  props
) => {
  return <ContextualMenuItem {...props} />;
};

const dialogStyles = { main: { maxWidth: 450 } };
const dragOptions = {
  moveMenuItemText: "Move",
  closeMenuItemText: "Close",
  menu: ContextualMenu,
  keepInBounds: true,
};

export function GuestManager(props: GuestManagerProps) {
  const options: IComboBoxOption[] = [
    {
      key: "Header1",
      text: "As Owner",
      itemType: SelectableOptionMenuItemType.Header,
    },
    { key: "A", text: "M365 Owners Group" },
    {
      key: "divider",
      text: "-",
      itemType: SelectableOptionMenuItemType.Divider,
    },
    {
      key: "Header2",
      text: "As Member",
      itemType: SelectableOptionMenuItemType.Header,
    },
    { key: "E", text: "M365 Members Group" },
  ];
  const spooptions: IComboBoxOption[] = [
    {
      key: "Header1",
      text: "Add to SharePoint Group",
      itemType: SelectableOptionMenuItemType.Header,
    },
    { key: "A", text: "Owners" },
    { key: "A", text: "Site Collection Admins" },
    { key: "A", text: "Visitors" },
    { key: "A", text: "Suppliers" },
    { key: "A", text: "Intel Workers" },
  ];
  const comboBoxRef = React.useRef<IComboBox | null>(null);
  const buttonStyles: Partial<IButtonStyles> = {
    root: { display: "block", margin: "10px 0 20px" },
  };
  class ownerObj {
    email!: string;
    isValid!: boolean;
  }

  const { instance, accounts } = useMsal();
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  MgtPerson.config.useContactApis = true;
  let selectedOwners: Array<directoryObject> = [];
  const [hidePickerErrorSummary, setHidePickerErrorSummary] = useState(true);
  const [checkedOwners, setCheckedOwners] = useState<string[]>([]);
  const [errorSummaryText, setErrorSummaryText] = useState("");
  const [hideOwnerSelectionDialog, { toggle: toggleOwnerSelectionHideDialog }] =
    useBoolean(true);
  const [isDraggable] = useBoolean(false);
  const labelId: string = useId("dialogLabel");
  const subTextId: string = useId("subTextLabel");
  const modalProps = React.useMemo(
    () => ({
      titleAriaId: labelId,
      subtitleAriaId: subTextId,
      isBlocking: false,
      styles: dialogStyles,
      dragOptions: isDraggable ? dragOptions : undefined,
    }),
    [isDraggable, labelId, subTextId]
  );
  const ownerDialogContentProps = {
    type: DialogType.normal,
    title: `Add Guests`,
    closeButtonAriaLabel: "Close",
    subText: `Manage guests for this workspace`,
  };
  function handleOwnerAddDialog() {
    setHidePickerErrorSummary(true);
    setErrorSummaryText("");
    toggleOwnerSelectionHideDialog();
  }
  function renderOwnerRemovalWarning(ownerList: string[]): JSX.Element {
    return (
      <div>
        <h5>Remove the selected {ownerList.length === 1 ? "guest" : "guests"}?</h5>
        <div
          style={{
            display: ownerList.includes((window as any).CURRENT_USER_EMAIL)
              ? "block"
              : "none",
          }}
        >
          <br></br>
          <div className="alert alert-warning" role="alert">
            <strong>Warning: </strong> You are attempting to remove yourself;
            you will no longer be able to manage this workspace.
          </div>
        </div>
      </div>
    );
  }
  function triggerLeaseExtend() {
    const accessTokenRequest = {
      scopes: apiScopes.resourceManagementScopes,
      account: accounts[0],
    };
    instance
      .acquireTokenSilent(accessTokenRequest)
      .then(async (accessTokenResponse) => {
        const toastLeaseRenewId = toast.loading("Updating resource lease");
        let operationResult = await extendResourceLease(
          accessTokenResponse.accessToken,
          props.isM365
            ? props.resourceId
            : encodeURIComponent(props.spoSiteUrl),
          props.isM365
        );
        if (operationResult === StatusCode.SuccessOK) {
          props.refreshDatasetFunction(
            props.isM365 ? props.resourceId : props.spoSiteUrl,
            ChangeType.UpdateLease,
            null
          );

          forceUpdate();
          toast.update(toastLeaseRenewId, {
            render: "Operation Complete",
            type: "success",
            isLoading: false,
            autoClose: 8000,
          });
        } else {
          toast.update(toastLeaseRenewId, {
            render: errorsTypes.somethingWentWrong.errorSubtitle,
            type: "error",
            isLoading: false,
            autoClose: 8000,
          });
        }
      });
  }
  function handleOwnerCheckColumn(ownerId: string, isChecked: boolean) {
    if (isChecked) {
      if (checkedOwners.includes(ownerId) === false) {
        setCheckedOwners([...checkedOwners, ownerId]);
      }
    } else {
      setCheckedOwners(
        checkedOwners.filter((email) => {
          return email !== ownerId;
        })
      );
    }
  }

  function handleOwnerRemovalDialog() {
    let selectedOwners: directoryObject[] = [];
    if (checkedOwners.length > 0) {
      Swal.fire({
        html: renderToStaticMarkup(renderOwnerRemovalWarning(checkedOwners)),
        showCancelButton: true,
        confirmButtonColor: "#CE0000",
        confirmButtonText: "Remove",
      }).then((result) => {
        if (result.isConfirmed) {
          const accessTokenRequest = {
            scopes: apiScopes.graphScopes,
            account: accounts[0],
          };
          instance
            .acquireTokenSilent(accessTokenRequest)
            .then((accessTokenResponse) => {
              // Acquire token silent success
              let accessToken = accessTokenResponse.accessToken;
              // Map over the array and call the async function for each elements
              const promises = checkedOwners.map(async (ownerEmail) => {
                try {
                  let userIdResponse = await getUserByEmail(
                    accessToken,
                    ownerEmail,
                    true
                  );
                  var responseObj = userIdResponse.value as any[];
                  if (responseObj && responseObj.length > 0) {
                    let currentUserObj: directoryObject = {};
                    currentUserObj.userPrincipalName = ownerEmail;
                    currentUserObj.id = responseObj[0].id;
                    selectedOwners.push(currentUserObj);
                  }
                } catch {
                  console.error(`Error while retrieving ID for ${ownerEmail}`);
                }
              });
              Promise.all(promises).then(() => {
                setAllCheckboxesByClass(
                  `chk-guests-${props.resourceId}`,
                  false
                );
                props.removeFunction(selectedOwners, props.resourceId);
              });
            });
        }
      });
    }
  }

  function searchEmail(emailParam: string): boolean {
    for (let index = 0; index < props.currentOwners.length; index++) {
      let currentOwner = props.currentOwners[index];
      if (
        currentOwner.mail?.trim().toLowerCase() ===
        emailParam.trim().toLowerCase()
      ) {
        return true;
      }
    }
    return false;
  }
  const _items: ICommandBarItemProps[] = [
    /*{
      key: "add",
      text: `Add New Guests`,
      iconProps: { iconName: "Add" },
      onClick: () => {
        handleOwnerAddDialog();
      },
      disabled: props.isAGS,
    },*/
    {
      key: "confirm",
      text: `Confirm Audit`,
      iconProps: { iconName: "SkypeCircleCheck" },
      onClick: () => {
        triggerLeaseExtend();
      },
      disabled: props.isAGS || props.ownerCount < 2,
    },
    {
      key: "remove",
      text: "Remove",
      iconProps: { iconName: "Cancel" },
      onClick: () => {
        handleOwnerRemovalDialog();
      },
      disabled: props.currentOwners.length < 1 || props.isAGS,
    },
    {
      key: "refresh",
      text: "Refresh",
      iconProps: { iconName: "Refresh" },
      onClick: () => {
        Swal.showLoading();
        const randomNum: number = Math.floor(Math.random() * (5 - 2 + 1)) + 2;
        setTimeout(() => {
          Swal.close();
        }, randomNum * 1000);
      },
    },
    {
      key: "feedback",
      text: "Feedback",
      iconProps: { iconName: "Feedback" },
      onClick: () => {
        const feedbackLinkElement = document.getElementById(
          "btnOpenFeedbackForm"
        );
        feedbackLinkElement?.click();
      },
    },
  ];
  const overflowProps: IButtonProps = {
    menuProps: {
      contextualMenuItemAs: CustomMenuItem,
      // Styles are passed through to menu items here

      items: [], // CommandBar will determine items rendered in overflow
      isBeakVisible: true,
      beakWidth: 20,
    },
  };
  return (
    <div>
      <Dialog
        hidden={hideOwnerSelectionDialog}
        onDismiss={toggleOwnerSelectionHideDialog}
        dialogContentProps={ownerDialogContentProps}
        modalProps={modalProps}
      >
        <div className="row">
          <div className="col">
            <div>
              <PeoplePicker
                type={PersonType.person}
                userType={UserType.user}
                className="mgt-light light-border"
                userFilters={`userType eq 'Member' and mail ne '${
                  (window as any).CURRENT_USER_EMAIL
                }'`}
                showMax={10}
                selectionMode="multiple"
                selectionChanged={(event: any) => {
                  selectedOwners = [];
                  let selectedValue: Array<directoryObject> = event[
                    "detail"
                  ] as Array<directoryObject>;
                  if (selectedValue.length > 0) {
                    selectedOwners = selectedValue;
                    setHidePickerErrorSummary(true);
                    setErrorSummaryText("");
                  } else {
                    setErrorSummaryText(
                      `No guest was selected`
                    );
                    setHidePickerErrorSummary(false);
                  }
                }}
              ></PeoplePicker>
              <ComboBox
                componentRef={comboBoxRef}
                defaultSelectedKey="C"
                label="Add Guest to:"
                options={props.isM365 ? options : spooptions}
              />
              <div
                className="invalid-feedback"
                style={{ display: hidePickerErrorSummary ? "none" : "grid" }}
              >
                {errorSummaryText}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <PrimaryButton
            onClick={() => {
              let filteredOwners: directoryObject[] = [];
              if (selectedOwners.length > 0) {
                selectedOwners.forEach((currOwner) => {
                  if (
                    searchEmail(
                      currOwner.userPrincipalName
                        ?.trim()
                        .toLowerCase() as string
                    ) === false
                  ) {
                    filteredOwners.push(currOwner);
                  }
                });
                if (filteredOwners.length > 0) {
                  toggleOwnerSelectionHideDialog();
                  props.addFunction(filteredOwners, props.resourceId);
                } else {
                  toast.info(
                    `The selected users are already ${props.isM365 ? "owners" : "site administrators"}`,
                    {
                      type: "info",
                      isLoading: false,
                      autoClose: 8000,
                    }
                  );
                }
              }
            }}
            text="Confirm"
          />
          <DefaultButton
            onClick={() => {
              setHidePickerErrorSummary(true);
              setErrorSummaryText("");
              toggleOwnerSelectionHideDialog();
            }}
            text="Cancel"
          />
        </DialogFooter>
      </Dialog>
      <div>
        <br />
        <div
          style={{
            paddingLeft: "14px",
            paddingRight: "14px",
            display: props.currentOwners.length > 0 ? "grid" : "none",
          }}
        >
          <div
            className="alert alert-light"
            style={{ backgroundColor: "white" }}
            role="alert"
          >
            {" "}
            Please review your guest list and remove anyone who no longer needs
            access. <br></br>Once you’re confident the list is accurate, click{" "}
            <FontIcon
              aria-label="SkypeCircleCheck"
              iconName="SkypeCircleCheck"
              className={mergeStyles({
                fontSize: 15,
                height: 25,
                width: 25,
                margin: "0 2px",
              })}
            />
            <strong>Confirm Audit</strong> to finalize the review and renew your
            workspace lease.
          </div>
        </div>
        <div
          style={{
            paddingLeft: "14px",
            paddingRight: "14px",
            display: props.currentOwners.length > 0 ? "none" : "grid",
          }}
        >
          <div className="alert alert-success" role="alert">
            Your workspace currently has no guest users. No further action is
            required. Simply click{" "}
            <FontIcon
              aria-label="SkypeCircleCheck"
              iconName="SkypeCircleCheck"
              className={mergeStyles({
                fontSize: 15,
                height: 25,
                width: 25,
                margin: "0 2px",
              })}
            />
            <strong>Confirm Audit</strong> to complete the review and extend
            your workspace lease.
          </div>
        </div>
                <div
          style={{
            paddingLeft: "14px",
            paddingRight: "14px",
            display: props.ownerCount < 2 ? "grid" : "none",
          }}
        >
          <div className="alert alert-success" role="alert">
          Your workspace has less than two owners, before submiting the audit make sure at least 2 owners of site collection admins are added
          </div>
        </div>
        <div
          style={{
            paddingLeft: "14px",
            paddingRight: "14px",
            display: props.isAGS ? "grid" : "none",
          }}
        >
          <div className="alert alert-warning" role="alert">
            {" "}
            <strong>⚠</strong>
            This site is managed by the AGS system, go{" "}
            <a
              href="https://ags.intel.com/identityiq/home.jsf"
              target="_blank"
              className="alert-link"
              rel="noopener noreferrer"
            >
              HERE
            </a>{" "}
            if you require to manage ownership of this space
          </div>
        </div>

        <div className="row">
          <div className="col">
            <br />
            <div
              style={{
                paddingLeft: "14px",
                paddingRight: "14px",
              }}
            >
              <CommandBar
                items={_items}
                overflowButtonProps={overflowProps}
                ariaLabel="Inbox actions"
                primaryGroupAriaLabel="Email actions"
                farItemsGroupAriaLabel="More actions"
              />
              <hr style={{ color: "rgb(233, 233, 233)", margin: "0" }}></hr>
              <div
                style={{
                  width: "100%",
                  backgroundColor: "white",
                  paddingTop: "10px",
                  paddingBottom: "10px",
                }}
              >
                <div className="col-sm-3"></div>
              </div>
              <table
                className="table"
                style={{
                  backgroundColor: "white",
                  display: props.currentOwners.length > 0 ? "table" : "none",
                }}
              >
                <thead>
                  <tr style={{ textAlign: "justify" }}>
                    <th>
                      <input
                        type="checkbox"
                        style={{ width: "20px", height: "20px" }}
                        disabled={props.isAGS}
                        onChange={(event) => {
                          setAllCheckboxesByClass(
                            `chk-guests-${props.resourceId}`,
                            event.target.checked as boolean
                          );
                        }}
                      />
                    </th>
                    <th>Name</th>
                    <th>User Type</th>
                    <th>User Source</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {props.currentOwners.map((owner: directoryObject, index) => {
                    return (
                      <tr key={index} style={{ textAlign: "justify" }}>
                        <td>
                          <input
                            type="checkbox"
                            style={{ width: "20px", height: "20px" }}
                            disabled={props.isAGS}
                            className={`chk-guests-${props.resourceId}`}
                            onChange={(event) => {
                              handleOwnerCheckColumn(
                                owner.mail!,
                                event.target.checked
                              );
                            }}
                          />
                        </td>
                        <td style={{ width: "40%" }}>
                          <Person
                            fallbackDetails={{
                              initials: "EXT",
                              mail: owner.mail,
                              displayName: owner.mail,
                            }}
                            personCardInteraction={PersonCardInteraction.none}
                            avatarType="photo"
                            avatarSize="small"
                            view={ViewType.oneline}
                          ></Person>
                        </td>
                        <td style={{ width: "15%" }}>
                          <span className="badge rounded-pill text-bg-primary">
                            Guest
                          </span>{" "}
                        </td>
                        <td style={{ width: "15%" }}>
                          {owner.locations!.map((guest: string, index) => {
                            return (
                              <span className="badge rounded-pill text-bg-primary">
                                {guest}
                              </span>
                            );
                          })}
                        </td>
                        <td style={{ width: "45%" }}>
                          <Link href={owner.mail}>{owner.mail}</Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <br />
      </div>
    </div>
  );
}
